import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import Icon from '../components/ui/icon';
import { useAuth } from '../contexts/AuthContext';
import { orderApi } from '../services/orderApi';
import { Order, getOrderStatusText, getOrderStatusColor, formatCurrency, formatOrderDate, isOrderPendingOrProcessing } from '../types/order';
import OrderDetailsModal from '../components/ui/order-details-modal';

const Orders: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage] = useState(1);
  const [, setHasMore] = useState(true);
  
  // Modal state
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get filter from URL
  const filter = searchParams.get('filter') || 'all';

  // Load orders function
  const loadOrders = useCallback(async () => {
    if (!user?.outlet_id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      // Map filter to API status
      let statusFilter: string | undefined;
      switch (filter) {
        case 'pending':
          // PENDING và PROPOSED sẽ được filter ở client side vì API chỉ hỗ trợ 1 status
          statusFilter = undefined;
          break;
        case 'processing':
          // Lấy tất cả đơn rồi filter ở client để có cả CONFIRMED và SHIPPED
          statusFilter = undefined;
          break;
        case 'completed':
          // Lấy tất cả đơn rồi filter ở client để có cả DELIVERED và COMPLETED
          statusFilter = undefined;
          break;
        case 'cancelled':
          // Lấy tất cả đơn rồi filter ở client để có cả CANCELLED và RETURN
          statusFilter = undefined;
          break;
        default:
          statusFilter = undefined;
      }

      const response = await orderApi.getOrders(user.outlet_id, {
        page: currentPage,
        limit: 20,
        status: statusFilter,
        sort_by: 'updated_at',
        sort_order: 'desc'
      });

      if (response.success && response.data) {

        // Filter orders based on selected filter
        let filteredOrders = response.data.orders;
        switch (filter) {
          case 'pending':
            filteredOrders = response.data.orders.filter(order => 
              order.order_status === 'PENDING' || order.order_status === 'PROPOSED'
            );
            break;
        case 'processing':
          filteredOrders = response.data.orders.filter(order => 
            order.order_status === 'CONFIRMED' || order.order_status === 'SHIPPED'
          );
          break;
        case 'completed':
          filteredOrders = response.data.orders.filter(order => 
            order.order_status === 'DELIVERED' || order.order_status === 'COMPLETED'
          );
          break;
        case 'cancelled':
          filteredOrders = response.data.orders.filter(order => 
            order.order_status === 'CANCELLED' || order.order_status === 'RETURN'
          );
          break;
          default:
            // 'all' - no additional filtering
            break;
        }

        setOrders(filteredOrders);
        
        // Handle pagination if available
        if (response.data.pagination) {
          setHasMore(response.data.pagination.has_more);
        } else {
          setHasMore(false); // No pagination info, assume no more pages
        }
      } else {
        throw new Error(response.message || 'Không thể tải danh sách đơn hàng');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Không thể tải danh sách đơn hàng');
    } finally {
      setIsLoading(false);
    }
  }, [user?.outlet_id, currentPage, filter]);

  // Load orders on mount and when dependencies change
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Handle order details view
  const handleViewDetails = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  // Handle edit order action
  const handleEditOrder = (orderId: string) => {
    const message = `Gửi lại thông tin chi tiết đơn hàng ${orderId} để tôi điều chỉnh nhé!`;
    
    // Navigate to Assistant page with pre-filled message
    navigate('/assistant', { 
      state: { 
        initialMessage: message 
      } 
    });
  };


  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-3">📦 Đơn hàng</h1>
        <p className="text-lg text-muted-foreground">
          {filter === 'pending' ? 'Đơn hàng đang chờ xử lý' : 
           filter === 'processing' ? 'Đơn hàng đang được xử lý' :
           filter === 'completed' ? 'Đơn hàng đã hoàn tất' :
           filter === 'cancelled' ? 'Đơn hàng đã hủy' :
           'Quản lý đơn hàng và theo dõi trạng thái'}
        </p>
        
        {/* Filter Tabs */}
        <div className="flex justify-center mt-4">
          <div className="flex bg-secondary rounded-lg p-1 overflow-x-auto">
            <button
              onClick={() => navigate('/orders?filter=all')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                filter === 'all' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Tất cả
            </button>
            <button
              onClick={() => navigate('/orders?filter=pending')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                filter === 'pending' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Đang chờ
            </button>
            <button
              onClick={() => navigate('/orders?filter=processing')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                filter === 'processing' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Đang xử lý
            </button>
            <button
              onClick={() => navigate('/orders?filter=completed')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                filter === 'completed' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Hoàn tất
            </button>
            <button
              onClick={() => navigate('/orders?filter=cancelled')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                filter === 'cancelled' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Hủy
            </button>
          </div>
        </div>
        
      </div>

      {/* Error State */}
      {error && (
        <Card className="text-center py-12">
          <CardContent>
            <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Lỗi tải dữ liệu</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Thử lại
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <Card key={index} className="animate-pulse">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="h-6 bg-muted rounded w-32 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-24"></div>
                  </div>
                  <div className="h-6 bg-muted rounded w-20"></div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-5 bg-muted rounded"></div>
                      <div className="h-4 bg-muted rounded w-24"></div>
                    </div>
                    <div className="h-6 bg-muted rounded w-20"></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 bg-muted rounded"></div>
                    <div className="h-4 bg-muted rounded w-32"></div>
                  </div>
                  <div className="flex gap-3 pt-3">
                    <div className="h-10 bg-muted rounded flex-1"></div>
                    <div className="h-10 bg-muted rounded w-24"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Orders List */}
      {!isLoading && !error && (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.order_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xl font-bold mb-2">#{order.order_id}</CardTitle>
                    <p className="text-base text-muted-foreground">
                      Ngày đặt: {formatOrderDate(order.created_at)}
                    </p>
                  </div>
                  <Badge className={`${getOrderStatusColor(order.order_status)} text-sm px-3 py-1 flex-shrink-0`}>
                    {getOrderStatusText(order.order_status)}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Icon name="package" size={20} className="text-blue-500" />
                      <span className="text-base font-medium">{order.items_count} sản phẩm</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-xl text-green-600">
                        {formatCurrency(order.final_amount)}
                      </div>
                    </div>
                  </div>
                  
                  {order.eta && (
                    <div className="flex items-center gap-3 text-base">
                      <Icon name="truck" size={20}  className="text-orange-500"/>
                      <span className="text-base font-medium">Dự kiến giao: {formatOrderDate(order.eta)}</span>
                    </div>
                  )}
                  
                   <div className="flex gap-2 pt-3">
                     <Button 
                       size="sm" 
                       variant="blueLight" 
                       className="flex-1 text-xs py-2 px-2"
                       onClick={() => handleViewDetails(order.order_id)}
                     >
                       <Icon name="eye" size={14}/>
                       Chi tiết
                     </Button>
                     {isOrderPendingOrProcessing(order.order_status) && (
                       <>
                         <Button 
                           size="sm" 
                           variant="orange"
                           className="flex-1 text-xs py-2 px-2"
                           onClick={() => handleEditOrder(order.order_id)}
                         >
                           <Icon name="edit" size={14}/>
                           Chỉnh sửa
                         </Button>
                         <Button 
                           size="sm" 
                           variant="green"
                           className="flex-1 text-xs py-2 px-2"
                           onClick={() => handleViewDetails(order.order_id)}
                         >
                           <Icon name="check-circle" size={14}/>
                           Xác nhận
                         </Button>
                       </>
                     )}
                   </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && orders.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Icon name="shopping-cart" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Chưa có đơn hàng</h3>
            <p className="text-muted-foreground">Hãy đặt hàng để xem danh sách ở đây</p>
          </CardContent>
        </Card>
      )}

      {/* Order Details Modal */}
      <OrderDetailsModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        orderId={selectedOrderId}
        outletId={user?.outlet_id || ''}
        onOrderUpdated={loadOrders}
      />
    </div>
  );
};

export default Orders;